import { useContext } from '@/compat';
import { computed, ref } from 'vue';
import isFileGif from './isFileGif';
import useThumbnail from './useThumbnail';
import useMediaExpansionPanels from '../useMediaExpansionPanels';

const useCoverImage = ({
  selectedImage,
  usingCrop,
  settings,
  allowImage,
  allowGif,
  imageFileExtensions,
  imageFileAccepts,
  gifFileExtensions,
  gifFileAccepts,
}) => {
  const { $set } = useContext();
  const phoMediaCardInformationRef = ref(null);
  const coverMediaExpansionPanels = useMediaExpansionPanels();
  const isCoverImageValidationErrorFound = ref(false);

  const editText = async image => {
    const imageObjectToEdit = {
      text: image?.text || '',
      reporter: image?.reporter || '',
      source: image?.source || '',
    };

    const result = await phoMediaCardInformationRef.value.edit(imageObjectToEdit);

    if (!result) {
      return;
    }

    $set(image, 'text', result.text);
    $set(image, 'reporter', result.reporter);
    $set(image, 'source', result.source);
  };

  const uploaderFileExtensions = [
    ...(allowImage ? [imageFileExtensions] : []),
    ...(allowGif ? [gifFileExtensions] : []),
  ].join(',');

  const uploaderFileAccepts = [
    ...(allowImage ? [imageFileAccepts] : []),
    ...(allowGif ? [gifFileAccepts] : []),
  ].join(',');

  const isSelectedCoverImageGif = computed(
    () => !!selectedImage.value && !!selectedImage.value.path && isFileGif(selectedImage.value.path),
  );

  const isCoverImageSelectedCorrectly = computed(() => selectedImage.value
    && (isSelectedCoverImageGif.value || (usingCrop ? selectedImage.value.crops : selectedImage.value.path)));

  const isDescriptionRequired = computed(() => !isFileGif(selectedImage.value?.path || '') && settings.isDescriptionRequired);

  const expansionPanelHeaderColor = computed(() => {
    if (isCoverImageSelectedCorrectly.value) return 'success';

    return isCoverImageValidationErrorFound.value ? 'error' : '';
  });

  const thumbnail = useThumbnail(
    selectedImage,
    img => img.value && img.value.path
      && (isFileGif(img.value.path) || !usingCrop ? img.value.path : (img.value.crops && img.value.crops[0]?.path)),
  );

  if (!coverMediaExpansionPanels.openedItems.length) {
    coverMediaExpansionPanels.openedItems.push(0);
  }

  const onValidationFailed = () => {
    isCoverImageValidationErrorFound.value = true;
  };

  return {
    phoMediaCardInformationRef,
    coverMediaExpansionPanels,
    isCoverImageValidationErrorFound,
    editText,
    uploaderFileExtensions,
    uploaderFileAccepts,
    isSelectedCoverImageGif,
    isCoverImageSelectedCorrectly,
    isDescriptionRequired,
    expansionPanelHeaderColor,
    thumbnail,
    onValidationFailed,
  };
};

export default useCoverImage;
